import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

/* =========================================================== */
/* ============ CINEMATIC INTRO (once per session) =========== */
/* Full-screen owner-supplied footage, muted autoplay, no
   controls, no loop. CHERUBIM wordmark + VENGEANCE FROM ABOVE
   fade in during the shot, out near the end; the overlay then
   fades into the already-loaded homepage beneath it. */
/* =========================================================== */

const EASE = [0.16, 1, 0.3, 1];
const FADE_OUT_MS = 1000;

const Intro = ({ onFinish }) => {
  const videoRef = useRef(null);
  const timers = useRef([]);
  const done = useRef(false);
  const [showTitle, setShowTitle] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const finish = () => {
    if (done.current) return;
    done.current = true;
    try { sessionStorage.setItem("cb_intro_seen", "1"); } catch {}
    setShowTitle(false);
    setLeaving(true);
    timers.current.push(setTimeout(onFinish, FADE_OUT_MS));
  };

  /* Muted autoplay attempt + one-gesture fallback (browser autoplay rule) */
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    window.addEventListener("pointerdown", tryPlay, { once: true });
    window.addEventListener("keydown", tryPlay, { once: true });
    return () => {
      window.removeEventListener("pointerdown", tryPlay);
      window.removeEventListener("keydown", tryPlay);
    };
  }, []);

  /* Lock page scroll while the intro holds the screen */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      document.body.style.overflow = prev;
      window.__lenis?.start();
      timers.current.forEach(clearTimeout);
    };
  }, []);

  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v || !isFinite(v.duration)) return;
    const d = v.duration;
    timers.current = [
      setTimeout(() => setShowTitle(true), Math.min(1.0, d * 0.2) * 1000),       /* title in */
      setTimeout(() => setShowTitle(false), Math.max(d - 1.3, d * 0.6) * 1000),  /* title out near end */
      setTimeout(finish, (d + 1.5) * 1000),                                      /* safety if 'ended' never fires */
    ];
  };

  return (
    <motion.div
      data-testid="cinematic-intro"
      role="presentation"
      className="fixed inset-0 z-[300] w-screen h-[100dvh] bg-black overflow-hidden"
      initial={{ opacity: 1 }}
      animate={{ opacity: leaving ? 0 : 1 }}
      transition={{ duration: leaving ? FADE_OUT_MS / 1000 : 0.3, ease: "easeInOut" }}
    >
      <video
        ref={videoRef}
        poster="/assets/video/intro-poster.jpg"
        muted
        playsInline
        autoPlay
        preload="auto"
        disablePictureInPicture
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={finish}
        onError={finish}
        className="absolute inset-0 w-full h-full object-cover select-none"
        style={{ objectPosition: "center" }}
        data-testid="intro-video"
      >
        <source src="/assets/video/intro.webm" type="video/webm" />
        <source src="/assets/video/intro.mp4" type="video/mp4" />
      </video>

      {/* Title — only elements over the footage: wordmark + subtitle */}
      <motion.div
        className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
        initial={false}
        animate={{ opacity: showTitle ? 1 : 0, y: showTitle ? 0 : 10 }}
        transition={{ duration: 1.1, ease: EASE }}
        data-testid="intro-title"
      >
        {/* soft cinematic scrim so the gold reads over bright footage —
            fades in and out with the title */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse 62% 44% at 50% 52%, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.18) 55%, transparent 78%)" }}
        />
        <img
          src="/assets/art/cherubim-logo.png"
          alt="CHERUBIM"
          draggable="false"
          className="h-auto w-[68vw] max-w-[520px] select-none"
          style={{ filter: "drop-shadow(0 4px 28px rgba(0,0,0,0.85))" }}
        />
        <div
          className="font-caps-wide text-[10px] sm:text-[12px] text-[var(--cb-gold-soft)] mt-5"
          style={{ letterSpacing: "0.46em", textShadow: "0 2px 18px rgba(0,0,0,0.85)" }}
        >
          VENGEANCE FROM ABOVE
        </div>
      </motion.div>

      {/* Skip / Enter with sound */}
      <div className="absolute bottom-6 right-6 flex items-center gap-3">
        <button
          type="button"
          onClick={finish}
          data-testid="intro-skip"
          className="font-caps text-[10px] tracking-[0.3em] text-[rgba(197,160,89,0.75)] hover:text-[#E2C37A] border border-[rgba(197,160,89,0.35)] hover:border-[rgba(226,195,122,0.6)] bg-black/40 px-4 py-2.5 transition-colors duration-300 cursor-pointer"
        >
          SKIP INTRO
        </button>
        <button
          type="button"
          onClick={() => { window.dispatchEvent(new Event("cherubim:start-score")); finish(); }}
          data-testid="intro-enter-sound"
          className="font-caps text-[10px] tracking-[0.3em] bg-[var(--cb-gold)] hover:bg-[var(--cb-gold-soft)] text-[var(--cb-bg)] px-4 py-2.5 transition-colors duration-300 cursor-pointer flex items-center gap-2"
        >
          ENTER WITH SOUND
        </button>
      </div>
    </motion.div>
  );
};

export default Intro;
